import React from "react";
import {
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  Icon,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import {
  NEEDS_ATTENTION_FILTER,
  sectionPath,
  snapshotTimestampToDate,
} from "./investigationUtils";
import { GroupsFilterQuery } from "./groupsFilter";
import { formatWhen } from "./formatWhen";
import "./InvestigationFunnel.scss";

/** The page each funnel column opens (a subset of the side-navigation sections). */
export type FunnelSection =
  | "backlog"
  | "active"
  | "mine"
  | "unowned"
  | "resolved";

export const FUNNEL_LABELS = {
  backlog: "Not yet investigated",
  active: "Under investigation",
  needsOwner: "Needs an owner",
  resolved: "Resolved",
} as const;

export interface FunnelBacklogStats {
  loading: boolean;
  error?: string;
  /** False until the hourly reconciliation has run for this stage. */
  available?: boolean;
  items: number;
  /** Total reconciled groups available to browse (server-paged in the backlog table). */
  groups: number;
  impactUsd?: number | null;
  snapshotTimestamp?: string;
}

export interface FunnelCaseStats {
  loading: boolean;
  cases: number;
  /** More pages exist on the server than were counted. */
  more: boolean;
  items: number;
  /** Null when no case has been reconciled yet. */
  impactUsd: number | null;
  /** Investigations the viewer owns with unread notifications. */
  attention?: number;
  /** Active investigations nobody holds. */
  needsOwner?: number;
}

interface InvestigationFunnelProps {
  /** Stage the counts are for; the column links carry it so they are shareable. */
  stage: string;
  backlog: FunnelBacklogStats;
  active: FunnelCaseStats;
  resolved: Pick<
    FunnelCaseStats,
    "loading" | "cases" | "more" | "items" | "impactUsd"
  >;
  /** Opens the page a column counts; every headline number is a link to it. */
  onOpenSection: (section: FunnelSection, filter?: GroupsFilterQuery) => void;
}

/** "$25.7M" — tiles are for scanning, so impact is compact; tables carry the exact figure. */
const compactUsd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});
export const formatCompactUsd = (v?: number | null): string =>
  v === undefined || v === null ? "" : compactUsd.format(v);

const plural = (n: number, word: string) =>
  `${n.toLocaleString()} ${word}${n === 1 ? "" : "s"}`;

/** "View all 226 groups" — the one button on the funnel. */
export const browseLabel = (groups: number): string =>
  `View all ${plural(groups, "group")}`;

/**
 * The headline of a column: a spinner while counting, a "not reconciled" marker when the
 * hourly job has not run, otherwise the count.
 */
const countLine = (
  loading: boolean,
  text: string,
  pending?: boolean
): React.ReactNode => {
  if (loading)
    return <StatusIndicator type="loading">Counting…</StatusIndicator>;
  if (pending)
    return <StatusIndicator type="pending">Not reconciled yet</StatusIndicator>;
  return text;
};

const casesText = (stats: { cases: number; more: boolean }) =>
  `${stats.cases.toLocaleString()}${stats.more ? "+" : ""} ${
    stats.cases === 1 && !stats.more ? "case" : "cases"
  }`;

function describeBacklog(b: FunnelBacklogStats): string | undefined {
  if (b.error) return b.error;
  if (b.available === false)
    return "Appears after the first hourly reconciliation.";
  if (b.loading) return undefined;
  if (b.items === 0) return "Every DLQ item has an investigation.";
  const impact = formatCompactUsd(b.impactUsd);
  return [impact ? `${impact} impact` : null, plural(b.groups, "group")]
    .filter(Boolean)
    .join(" · ");
}

function describeActive(a: FunnelCaseStats): string | undefined {
  if (a.loading) return undefined;
  if (!a.cases) return "Nothing is being worked on.";
  const impact = formatCompactUsd(a.impactUsd);
  return [plural(a.items, "item"), impact ? `${impact} impact` : null]
    .filter(Boolean)
    .join(" · ");
}

/** "1,234 items · $5.6K impact" — what the resolved cases covered. */
function describeResolved(
  r: Pick<FunnelCaseStats, "loading" | "cases" | "items" | "impactUsd">
): string | undefined {
  if (r.loading || !r.cases) return undefined;
  const impact = formatCompactUsd(r.impactUsd);
  return [plural(r.items, "item"), impact ? `${impact} impact` : null]
    .filter(Boolean)
    .join(" · ");
}

interface ColumnProps {
  label: string;
  headline: React.ReactNode;
  /** Where the tile goes; a real URL so the tile is shareable and middle-clickable. */
  href: string;
  onOpen: () => void;
  /** One secondary line; keep it short. */
  detail?: React.ReactNode;
  /** Optional status line (attention). */
  flags?: React.ReactNode;
  /** The needs-owner count turns red when it is non-zero. */
  alarm?: boolean;
}

/** Plain click navigates in-app; modifier clicks keep the browser's open-in-new-tab. */
const inAppClick =
  (onOpen: () => void) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
      return;
    e.preventDefault();
    onOpen();
  };

/** One clickable tile; see InvestigationFunnel.scss for why it is a native anchor. */
const ColumnTile: React.FC<Omit<ColumnProps, "flags">> = ({
  label,
  headline,
  href,
  onOpen,
  detail,
  alarm,
}) => (
  <a
    className="funnel-tile"
    href={href}
    aria-label={`${label}: open`}
    onClick={inAppClick(onOpen)}
  >
    <SpaceBetween size="xxs">
      <Box variant="awsui-key-label">{label}</Box>
      <span
        className={[
          "funnel-tile__headline",
          typeof headline === "string" && "funnel-tile__headline--count",
          alarm && "funnel-tile__headline--alarm",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {headline}
      </span>
      {detail && (
        <Box color="text-body-secondary" fontSize="body-s">
          {detail}
        </Box>
      )}
    </SpaceBetween>
  </a>
);

/**
 * A tile plus its flags. Flags render below the tile rather than inside it, so a flag can be a
 * link of its own (anchors can't nest).
 */
const Column: React.FC<ColumnProps> = ({ flags, ...tile }) => (
  <SpaceBetween size="xxs">
    <ColumnTile {...tile} />
    {flags}
  </SpaceBetween>
);

export const InvestigationFunnel: React.FC<InvestigationFunnelProps> = ({
  stage,
  backlog,
  active,
  resolved,
  onOpenSection,
}) => {
  const notReconciled = backlog.available === false;
  const canBrowse =
    !backlog.error && !notReconciled && (backlog.loading || backlog.groups > 0);

  const backlogDetail = describeBacklog(backlog);
  const activeDetail = describeActive(active);

  // The flag opens the active table filtered to exactly the cases it counts.
  const activeFlags = active.attention ? (
    <a
      className="funnel-flag"
      href={sectionPath("active", stage, NEEDS_ATTENTION_FILTER)}
      aria-label="Show investigations that need my attention"
      onClick={inAppClick(() =>
        onOpenSection("active", NEEDS_ATTENTION_FILTER)
      )}
    >
      <Icon name="status-warning" variant="warning" />
      <span className="funnel-flag__text">
        {active.attention.toLocaleString()} need
        {active.attention === 1 ? "s" : ""} your attention
      </span>
    </a>
  ) : undefined;

  const needsOwner = active.needsOwner ?? 0;
  const needsOwnerLoading = active.loading && !active.cases;
  let needsOwnerDetail: string | undefined;
  if (!needsOwnerLoading && needsOwner > 0)
    needsOwnerDetail = "Take ownership from the table below.";
  else if (!needsOwnerLoading && active.cases)
    needsOwnerDetail = "Every active case has an owner.";

  return (
    <Container
      header={
        <Header
          variant="h2"
          description={
            backlog.snapshotTimestamp
              ? `DLQ snapshot ${formatWhen(
                  snapshotTimestampToDate(backlog.snapshotTimestamp)
                )} · refreshed hourly`
              : undefined
          }
          actions={
            canBrowse ? (
              <Button
                iconName="folder-open"
                onClick={() => onOpenSection("backlog")}
                disabled={backlog.loading}
              >
                {browseLabel(backlog.groups)}
              </Button>
            ) : undefined
          }
        >
          Where the DLQ stands
        </Header>
      }
    >
      <ColumnLayout columns={4} variant="text-grid">
        <Column
          label={FUNNEL_LABELS.backlog}
          href={sectionPath("backlog", stage)}
          onOpen={() => onOpenSection("backlog")}
          headline={
            backlog.error ? (
              <StatusIndicator type="warning">Unavailable</StatusIndicator>
            ) : (
              countLine(
                backlog.loading,
                plural(backlog.items, "item"),
                notReconciled
              )
            )
          }
          detail={backlogDetail}
        />
        <Column
          label={FUNNEL_LABELS.active}
          href={sectionPath("active", stage)}
          onOpen={() => onOpenSection("active")}
          headline={countLine(
            active.loading && !active.cases,
            casesText(active)
          )}
          detail={activeDetail}
          flags={activeFlags}
        />
        <Column
          label={FUNNEL_LABELS.needsOwner}
          href={sectionPath("unowned", stage)}
          onOpen={() => onOpenSection("unowned")}
          headline={countLine(
            needsOwnerLoading,
            casesText({ cases: needsOwner, more: false })
          )}
          alarm={!needsOwnerLoading && needsOwner > 0}
          detail={needsOwnerDetail}
        />
        <Column
          label={FUNNEL_LABELS.resolved}
          href={sectionPath("resolved", stage)}
          onOpen={() => onOpenSection("resolved")}
          headline={countLine(
            resolved.loading && !resolved.cases,
            casesText(resolved)
          )}
          detail={describeResolved(resolved)}
        />
      </ColumnLayout>
    </Container>
  );
};

export default InvestigationFunnel;
